import { Center, Flex, Spinner, useBoolean } from '@chakra-ui/react';
import { IconSearchData } from 'hooks/usePaginatedIcon/common/usePaginatedIcon.types';
import { UserType, useUser } from 'modules/User';
import { useToast } from 'modules/common/components/Toast';
import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { WidgetType } from 'types/widget.types';
import { E1_ICON_ID_TO_LINE_ICON_TYPE_MAP } from 'widgets/Lines/LineIconWidget/LineIconWidget.config';
import { AllWidgetData, NewWidget } from 'widgets/Widget.types';

import { IconPreview } from './IconPreview';
import { PartialIconMetadata } from './IconWidgetMenu.types';
import { LineIconPreview } from './LineIconPreview';

type OnClickIconWidget = (id: string, viewBox: string, mono?: boolean, iconMetadata?: PartialIconMetadata) => void;
type CreateWidgetData = (id: string, viewBox: string, mono?: boolean, iconMetadata?: PartialIconMetadata) => NewWidget;
type OnClickNonIconWidget = (type: WidgetType, data: Partial<AllWidgetData>) => void;

interface IconListProps {
  icons: IconSearchData[];
  testId?: string;
  disableDragDrop?: boolean;
  onClickIconWidget: OnClickIconWidget;
  onClickNonIconWidget?: OnClickNonIconWidget;
  onOpenModal: () => void;
  setUpgradeCallback?: (callback: () => void, upgradePlan: UserType) => void;
  createWidgetData?: CreateWidgetData;
}

export const IconList = ({
  icons,
  disableDragDrop,
  testId = 'iconwidgetmenu-icon-list',
  onClickIconWidget,
  onClickNonIconWidget,
  setUpgradeCallback,
  onOpenModal,
  createWidgetData,
}: IconListProps): ReactElement => {
  const [isLoading, setIsLoading] = useBoolean(true);
  const toast = useToast();
  const { userType } = useUser();
  const { t } = useTranslation('editor_widget_menu', { useSuspense: false, keyPrefix: 'sideMenu.iconsMenu' });

  const onLoadError = () => {
    toast({ title: t('loadingError'), status: 'error' });
  };

  const onLoadIcon = () => {
    if (isLoading) {
      setIsLoading.off();
    }
  };

  const handleCreateWidgetData = (id: string, viewBox: string, icon: IconSearchData) => {
    if (!createWidgetData) return null;

    const iconMetadata =
      icon.iconPath || icon.iconIllustrationThumbnailUrl
        ? {
            iconPath: icon.iconPath || '',
            isIllustration: !!icon.iconIllustrationThumbnailUrl,
          }
        : undefined;

    return createWidgetData(id, viewBox, icon.isMono, iconMetadata);
  };

  const handleClickIconWidget = (id: string, viewBox: string, icon: IconSearchData) => {
    const iconMetadata =
      icon.iconPath || icon.iconIllustrationThumbnailUrl
        ? {
            iconPath: icon.iconPath || '',
            isIllustration: !!icon.iconIllustrationThumbnailUrl,
          }
        : undefined;

    onClickIconWidget(id, viewBox, icon.isMono, iconMetadata);
  };

  return (
    <>
      <Flex wrap='wrap' gap='6px' data-testid={testId} p={1} pr={0}>
        {icons.map((icon: IconSearchData) => {
          if (E1_ICON_ID_TO_LINE_ICON_TYPE_MAP[icon.id]) {
            return (
              <LineIconPreview
                key={icon.id}
                id={icon.id}
                onClick={onClickNonIconWidget}
                disableDragDrop={disableDragDrop}
              />
            );
          }
          return (
            <IconPreview
              id={icon.id}
              key={icon.id}
              userType={userType}
              permission={icon.permission}
              iconPath={icon.iconPath}
              iconThumbnailUrl={icon.iconIllustrationThumbnailUrl}
              isMono={icon.isMono}
              disableDragDrop={disableDragDrop}
              onClickIconWidget={(id: string, viewBox: string) => handleClickIconWidget(id, viewBox, icon)}
              onCreateWidgetData={(id: string, viewBox: string) => handleCreateWidgetData(id, viewBox, icon)}
              onLoad={onLoadIcon}
              setUpgradeCallback={setUpgradeCallback}
              onOpenModal={onOpenModal}
              onLoadError={onLoadError}
            />
          );
        })}
      </Flex>

      {isLoading && (
        <Center>
          <Spinner data-testid='icon-widgetmenu-list-spinner' alignSelf='center' color='var(--vg-colors-brand-500)' />
        </Center>
      )}
    </>
  );
};
